import { Icon } from '@iconify/react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { InputPrefix, InputSuffix, TextBox } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import useAuth from 'src/hooks/useAuth';
import useGuideMe from 'src/hooks/useGuideMe';
import useIsSuperAdminSession from 'src/hooks/useIsSuperAdminSession';
import { SuppliersContext } from 'src/sections/manage_suppliers/SuppliersContext';
import { exportSuppliersToExcel } from 'src/sections/manage_suppliers/utils/exportSuppliersToExcel';
import { useContextSelector } from 'use-context-selector';
import useGuideMeSteps from './GuideMeSteps';

const SuppliersHeader = () => {
  const { t } = useTranslation();
  const searchValue = useContextSelector(SuppliersContext, (ctx) => ctx.searchValue);
  const setSearchValue = useContextSelector(SuppliersContext, (ctx) => ctx.setSearchValue);
  const fetchSuppliersForExport = useContextSelector(
    SuppliersContext,
    (ctx) => ctx.fetchSuppliersForExport
  );
  const isExportingSuppliers = useContextSelector(
    SuppliersContext,
    (ctx) => ctx.isExportingSuppliers
  );
  const { companyName, companyId } = useAuth();
  const canExportToExcel = useIsSuperAdminSession();
  const guideMeSteps = useGuideMeSteps();
  const { guideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'manage-suppliers-guide-me',
  });

  const handleClearSearch = useCallback(() => {
    setSearchValue('');
  }, [setSearchValue]);

  const handleExportToExcel = useCallback(async () => {
    try {
      // No matching suppliers still exports the file, with the header row only
      const items = await fetchSuppliersForExport();
      exportSuppliersToExcel(items, companyName, companyId, t);
      toast.success(t('suppliersExportSuccess'));
    } catch (error) {
      console.error('[SuppliersHeader] Export failed', error);
      toast.error(t('suppliersExportError'));
    }
  }, [fetchSuppliersForExport, companyName, companyId, t]);

  return (
    <div className="mb-2 mt-4 flex w-full flex-wrap items-center justify-between gap-4 px-2 sm:px-4">
      <div className="flex min-w-0 flex-[1_1_100%] items-center gap-2 sm:min-w-[280px] sm:max-w-[550px] sm:flex-[1_1_350px]">
        <FieldWrapper className="flex flex-row items-center space-x-2 w-full">
          <Label
            editorId="manage-suppliers-filter-input"
            className="text-xs flex items-center whitespace-nowrap font-semibold"
          >
            {t('Filter By')}
          </Label>
          <TextBox
            id="manage-suppliers-filter-input"
            max={500}
            maxLength={500}
            size="medium"
            style={{ fontSize: '14px' }}
            placeholder={t('Enter Supplier Name / AP ID / External Supplier Name / Catalog')}
            width="100%"
            value={searchValue}
            onChange={(event) => setSearchValue(String(event.value ?? ''))}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            prefix={() => (
              <InputPrefix className="pl-2">
                <Icon height="18" width="18" color="#AAAAAA" icon="lucide:search" />
              </InputPrefix>
            )}
            suffix={() => (
              <InputSuffix className="pr-2">
                {searchValue && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    aria-label={t('clearSearch')}
                    className="flex items-center justify-center"
                  >
                    <Icon height="16" width="16" color="#AAAAAA" icon="lucide:x" />
                  </button>
                )}
              </InputSuffix>
            )}
          />
        </FieldWrapper>
      </div>
      <div className="ml-auto flex w-full flex-shrink-0 flex-wrap items-center justify-end gap-2 sm:w-auto">
        {canExportToExcel && (
          <Button
            color="secondary"
            onClick={handleExportToExcel}
            loading={isExportingSuppliers}
          >
            {t('exportToExcel')}
          </Button>
        )}
        {guideMe}
      </div>
    </div>
  );
};

export default SuppliersHeader;
